/**
 * The venue map's lifecycle (BON-538).
 *
 * A hook rather than three effects inside the component, because the component
 * is then only a container and a token check — and because the three effects
 * genuinely are one thing: create the map, keep its data in step, keep its
 * hover paint in step.
 *
 * The event handlers are registered ONCE, on load, and read the current props
 * through a ref. Re-registering them whenever a callback identity changed would
 * leak listeners on every render.
 *
 * FITTING IS DELIBERATELY RARE. The map frames its pins when a genuinely new
 * set arrives — first load, and a change of region scope — and then leaves the
 * view alone. Anything else takes the camera away from the person using it:
 * fitting on every data push meant a hover, or typing in the search box, threw
 * away the zoom they had just set. `fitKey` is the caller's name for "this is a
 * different dataset now"; the venue array changing is not, by itself, that.
 */
import mapboxgl, { Map as MapboxMap, NavigationControl } from 'mapbox-gl';
import { useEffect, useRef } from 'react';

import {
  bindDotEvents,
  DOTS_LAYER,
  FALLBACK_VIEW,
  fit,
  installDots,
  radiusFor,
  toGeoJSON,
  VENUES_SOURCE,
  type ColorOf,
} from './venue-map-layers';
import type { VenueRosterRow } from './venues-roster-api';

export interface UseVenueMapOptions {
  token: string | undefined;
  venues: readonly VenueRosterRow[];
  colorOf: ColorOf;
  /** Changes when the map is looking at a different dataset, and only then. */
  fitKey: string;
  hoveredId: string | null;
  onHoverVenue?: (id: string | null) => void;
  onSelectVenue?: (id: string) => void;
}

export function useVenueMap({
  token,
  venues,
  colorOf,
  fitKey,
  hoveredId,
  onHoverVenue,
  onSelectVenue,
}: UseVenueMapOptions): React.RefObject<HTMLDivElement> {
  // Cast: the element is attached before any effect below reads it, which is
  // what the non-null ref type asserts.
  const container = useRef<HTMLDivElement>(null) as React.RefObject<HTMLDivElement>;
  const map = useRef<MapboxMap | null>(null);
  const latest = useRef({ venues, colorOf, fitKey, onHoverVenue, onSelectVenue });
  latest.current = { venues, colorOf, fitKey, onHoverVenue, onSelectVenue };
  /** The last dataset actually framed. Null until the first one arrives, so an
   *  empty first response does not count as having fitted. */
  const fitted = useRef<string | null>(null);

  useEffect(() => {
    if (!container.current || map.current || !token) return;
    mapboxgl.accessToken = token;
    // The design's map is a fixed light basemap and stays bright in dark mode.
    // Ours follows the theme — a deliberate improvement, not a fidelity miss.
    const isDark = document.documentElement.classList.contains('dark');
    const created = new MapboxMap({
      container: container.current,
      style: isDark ? 'mapbox://styles/mapbox/dark-v11' : 'mapbox://styles/mapbox/light-v11',
      // Mapbox v3 defaults to a globe, which at national scope draws the Arctic
      // and South America around a continent of pins. The design's map is flat
      // and so is every mental model of "our venues", so: mercator.
      projection: 'mercator',
      ...FALLBACK_VIEW,
    });
    map.current = created;
    created.addControl(new NavigationControl({ showCompass: false }), 'top-left');

    created.on('load', () => {
      // The card's height is settled by layout, not by the time this ran, and
      // fitBounds works off the size the map THINKS it has — without this the
      // national view frames a container a few pixels tall and lands three zoom
      // levels too far out.
      created.resize();
      installDots(created, toGeoJSON(latest.current.venues, latest.current.colorOf));
      bindDotEvents(created, () => latest.current);
      // Only if the venues are already here; otherwise the effect below fits
      // as soon as they arrive.
      if (latest.current.venues.length > 0) {
        fit(created, latest.current.venues);
        fitted.current = latest.current.fitKey;
      }
    });

    return () => {
      created.remove();
      map.current = null;
    };
  }, [token]);

  // New pins. NOT a new camera — see the note above.
  useEffect(() => {
    const current = map.current;
    const source = current?.getSource(VENUES_SOURCE) as mapboxgl.GeoJSONSource | undefined;
    if (!current || !source) return;
    source.setData(toGeoJSON(venues, colorOf));
  }, [venues, colorOf]);

  // Frame the pins, once per dataset.
  useEffect(() => {
    const current = map.current;
    if (!current?.getLayer(DOTS_LAYER)) return;
    if (fitted.current === fitKey || venues.length === 0) return;
    fit(current, venues);
    fitted.current = fitKey;
  }, [fitKey, venues]);

  // Which pin is swollen is a PAINT change, not a data one — re-sending the
  // GeoJSON on every pointer move would re-run the source diff each time.
  useEffect(() => {
    const current = map.current;
    if (!current?.getLayer(DOTS_LAYER)) return;
    current.setPaintProperty(DOTS_LAYER, 'circle-radius', radiusFor(hoveredId));
  }, [hoveredId]);

  return container;
}
